import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Icon } from 'components/ui';
import { PageHeader } from 'components/layout/PageHeader';
import CirculerShadow from 'components/ui/CirculerShadow';
import { useSubscription } from 'contexts/SubscriptionContext';

interface TrainingItem {
  title: string;
  description: string;
  ctaText: string;
  ctaHref: string;
  icon: React.ReactNode;
}

interface TrainingCardProps {
  title: string;
  description: string;
  ctaText: string;
  ctaHref: string;
  icon: React.ReactNode;
  locked?: boolean;
  upgradeMessage?: string;
}

const trainingItems: TrainingItem[] = [
  {
    title: 'Upcoming Sessions',
    description: 'Join our live coaching sessions to learn directly from experts',
    ctaText: 'View Google Calendar',
    ctaHref: 'https://calendar.google.com',
    icon: <Icon type="google_calendar" className="w-6 h-6 text-blue-600" />,
  },
  {
    title: 'Previous Recordings',
    description: 'Watch past coaching sessions on our YouTube channel',
    ctaText: 'Watch on Youtube',
    ctaHref: 'https://www.youtube.com/@victoria.olsina',
    icon: <Icon type="youtube" className="w-6 h-6 text-red-600" />,
  },
  {
    title: '1:1 Coaching',
    description: 'Book personalized coaching sessions tailored to your needs',
    ctaText: 'Book through Calendly',
    ctaHref: 'https://calendly.com/victoria_olsina/',
    icon: <Icon type="calendly" className="w-6 h-6 text-green-600" />,
  },
];

const TrainingCard: React.FC<TrainingCardProps> = ({
  title,
  description,
  ctaText,
  ctaHref,
  icon,
  locked = false,
  upgradeMessage,
}) => {
  const navigate = useNavigate();

  if (locked) {
    return (
      <Card className="bg-white rounded-xl shadow-[0px_4px_10px_0px_rgba(0,0,0,0.08)] border-0 p-6 h-[108px] flex items-center justify-between opacity-75">
        <div className="flex items-center space-x-4 flex-1">
          {icon && <div className="p-2 rounded-lg bg-gray-50">{icon}</div>}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 leading-tight">{title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{upgradeMessage}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-purple-600 font-medium text-sm hover:text-purple-700 hover:underline transition-colors duration-200 flex-shrink-0 ml-6"
        >
          Upgrade Plan
        </button>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-xl shadow-[0px_4px_10px_0px_rgba(0,0,0,0.08)] border-0 p-6 h-[108px] flex items-center justify-between hover:shadow-[0px_6px_20px_0px_rgba(0,0,0,0.12)] transition-all duration-200">
      <div className="flex items-center space-x-4 flex-1">
        {icon && <div className="p-2 rounded-lg bg-gray-50">{icon}</div>}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 leading-tight">{title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 font-medium text-sm hover:text-blue-700 hover:underline transition-colors duration-200 flex-shrink-0 ml-6"
      >
        {ctaText}
      </a>
    </Card>
  );
};

const Training: React.FC = () => {
  const { hasAccess, loading, subscription } = useSubscription();
  const navigate = useNavigate();

  // Redirect if user doesn't have access to Training
  useEffect(() => {
    if (!loading && !hasAccess('training')) {
      console.log('[Training] Access denied, redirecting to dashboard');
      navigate('/dashboard/assistant', { replace: true });
    }
  }, [hasAccess, loading, navigate]);

  // Show loading state while checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Don't render if no access (will redirect)
  if (!hasAccess('training')) {
    return null;
  }

  // Check if user is on starter plan
  const isStarterPlan = subscription?.plan === 'starter';

  return (
    <div className="min-h-screen overflow-hidden relative">
      <div className="container z-[999] mx-auto px-4 py-8">
        {/* Header */}
        <PageHeader
          title="Training"
          subtitle="Access information about upcoming live coaching sessions and past recordings"
          backButtonText="Back to Dashboard"
          backButtonPath="/dashboard/assistant"
          showLogo
          showBackButton
        />

        {/* Training Cards */}
        <div className="max-w-4xl mx-auto space-y-5">
          {trainingItems.map((item, index) => {
            // Lock 1:1 Coaching for starter plan users
            const isOneOnOneCoaching = item.title === '1:1 Coaching';
            const locked = isStarterPlan && isOneOnOneCoaching;

            return (
              <TrainingCard
                key={index}
                title={item.title}
                description={item.description}
                ctaText={item.ctaText}
                ctaHref={item.ctaHref}
                icon={item.icon}
                locked={locked}
                upgradeMessage={
                  locked ? 'Upgrade your plan to get access to 1:1 Coaching' : undefined
                }
              />
            );
          })}
        </div>
      </div>

      {/* Decorative elements */}
      <CirculerShadow className="bg-purple-300/20 w-96 h-96 absolute -top-40 -right-20" />
      <CirculerShadow className="absolute w-96 h-96 -bottom-40 left-0" />
    </div>
  );
};

export default Training;
